import { ColoursObj, Typography } from "@eventr/common-storybook";
import { Stack } from "@mui/material";
import i18n from "i18n/i18next";
import { lazy, type ComponentType } from "react";
import { useBoundStore } from "state/bound.store";
import type { PaneGroupId, ScreenLayoutResolution } from "../screen-layout";

const named = <K extends string>(load: () => Promise<Record<K, ComponentType>>, name: K) =>
  lazy(() => load().then((module) => ({ default: module[name] })));

const HomeTrailingDefault = lazy(() =>
  import("app/home/components/home-has-itineraries").then((module) => ({ default: module.HomeTrailingDefault })),
);
const AccountProfile = named(() => import("app/account/profile/account-profile"), "AccountProfilePage");

export function PanePlaceholder({ tab }: { tab: PaneGroupId }) {
  // The team list shows members only once someone besides the viewer is on the itinerary.
  const hasTeamMembers = useBoundStore((s) => !s.noItineraries && s.selectedItineraryUsers()?.length > 1);
  if (tab === "team" && !hasTeamMembers) return null;
  const text =
    tab === "chat"
      ? i18n.t("two-pane.choose-conversation")
      : tab === "team"
        ? i18n.t("two-pane.choose-team-member")
        : i18n.t("two-pane.choose-item");
  return (
    <Stack sx={{ height: "100%", justifyContent: "center", alignItems: "center", padding: "24px" }}>
      <Typography variant="bodyRegular" color={ColoursObj.font.secondary}>
        {text}
      </Typography>
    </Stack>
  );
}

/** The trailing pane's content when no entry is chosen. */
export function TrailingDefault({
  tab,
  trailing,
}: {
  tab: PaneGroupId;
  trailing: ScreenLayoutResolution["trailingPane"];
}) {
  if (trailing === "blank") return null;
  if (trailing === "profile") return <AccountProfile />;
  if (trailing === "placeholder") return <PanePlaceholder tab={tab} />;
  return <HomeTrailingDefault show={trailing === "map" ? "map" : trailing === "summary" ? "summary" : undefined} />;
}
